import { Fragment, useState, useContext, useEffect } from "react"
import { ReducerContext } from "../../contexts/ReducerContext"
import { getMonthYear, getShortDate, getLongDate } from "../../utils/calendar"
import { handleSubmitPromise } from "../../utils/handleSubmitPromise"
import { saveControl } from "../../actions/control"
import { SmartForm, Select, LoadingBox, DefaultFooter } from "../extras/SmartForm"
import { ErrorMessage, SuccessMessage } from "../static/Messages"
import { CloseButton } from "../static/Buttons"
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip } from 'chart.js'
import { Bar } from 'react-chartjs-2'

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip)

export const ControlRow = ({ elem, eventHandlers }) => {
    return (
        <div className="om-event uk-grid-small uk-padding-small" uk-grid="" 
             style={{cursor: 'pointer'}} onClick={() => eventHandlers.onInfo(elem.uuid)}>
            <div className="om-event-date uk-width-1-5@l uk-width-1-5@m uk-width-1-2">
                <h1 className="om-event-highlight">{getMonthYear(elem.when)}</h1>
            </div>
            <div className="om-event-title uk-width-1-5@l uk-width-1-5@m uk-width-1-2">
                <h4>{elem.type.toUpperCase()}</h4>
                <ul><li><i className="fas fa-users"></i> {elem.missing.length} {elem.missing.length === 1 ? 'Ausencia' : 'Ausencias'}</li></ul>
            </div>
            <div className="om-event-desc uk-width-expand">
                <p className="om-event-element">⏰ {getLongDate(elem.when).split(',')[0]} a las {getShortDate(elem.when)}</p>
                <ul className="uk-align-right@l uk-align-center"><li>📍 {elem.loc}</li></ul>
            </div>
        </div>
    )
}

export const TakeAttendance = {
    Header: () => <Fragment><i className="fas fa-chalkboard-teacher"></i> Pasar lista</Fragment>,
    Body: ({ onClose, modalState }) => {
        const [, state, dispatch, user] = useContext(ReducerContext)
        const [selectedEvent, setSelectedEvent] = useState("")
        const [checkedUsers, setCheckedUsers] = useState([])
        const [err, setErr] = useState("")
        const [loading, setLoading] = useState(false)
        const [success, setSuccess] = useState(false)

        const recentEvents = state.events?.filter(e => {
            const now = Date.now()
            const when = e.when
            const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
            return when <= now && when >= (now - sevenDaysMs)
        }) || []

        useEffect(() => {
            if (modalState) {
                setSelectedEvent("")
                setCheckedUsers([])
                setErr("")
                setLoading(false)
                setSuccess(false)
            }
        }, [modalState])

        useEffect(() => {
            if (selectedEvent) {
                const existingControl = state.controls?.find(c => c.uuid === selectedEvent)
                if (existingControl) {
                    setCheckedUsers(existingControl.missing)
                } else {
                    setCheckedUsers([])
                }
            } else {
                setCheckedUsers([])
            }
        }, [selectedEvent, state.controls])

        const handleCheckboxChange = (member) => {
            setCheckedUsers(prev => {
                const exists = prev.find(u => u.user === member.user)
                if (exists) {
                    return prev.filter(u => u.user !== member.user)
                } else {
                    return [...prev, { user: member.user, instrument: member.instrument }]
                }
            })
        }

        const handleSubmit = (e) => {
            e.preventDefault()
            if (!selectedEvent) {
                setErr("Selecciona un evento...")
                return
            }
            const promise = new Promise((resolve, reject) => {
                dispatch(saveControl(user.token, { uuid: selectedEvent, missing: checkedUsers }), resolve, reject)
            })
            handleSubmitPromise(promise, setErr, setSuccess, setLoading)
        }

        const formatEventLabel = (e) => `[${e.type}] ${e.desc} - ${getMonthYear(e.when)} ${getShortDate(e.when)}`

        return (
            <div className="uk-modal-body om-body">
                <SmartForm onSubmit={handleSubmit} states={[loading, err, success]}>
                    <div className="uk-margin">
                        <Select className="uk-select" value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)}>
                            <option value="">Selecciona un evento...</option>
                            {recentEvents.map(e => (
                                <option key={e.uuid} value={e.uuid}>{formatEventLabel(e)}</option>
                            ))}
                        </Select>
                        <div className="uk-text-muted uk-text-small uk-text-justify uk-margin-small-top">
                            Sólo podrás pasar lista de los eventos de los últimos 7 días. Este control será guardado para cualquier posterior consulta.
                        </div>
                    </div>
                    {selectedEvent && (
                        <div className="uk-margin" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <table className="uk-table uk-table-small uk-table-divider uk-table-hover">
                                <tbody>
                                    {state.members?.map(member => {
                                        const isChecked = checkedUsers.some(u => u.user === member.user)
                                        return (
                                            <tr key={member.user}>
                                                <td>
                                                    <label style={{ display: 'block', cursor: 'pointer' }}>
                                                        <input className="uk-checkbox uk-margin-small-right" type="checkbox"
                                                               data-instrument={member.instrument}
                                                               checked={isChecked} onChange={() => handleCheckboxChange(member)} />
                                                        {member.surnames}, {member.name} <span className="uk-text-muted uk-text-small hide-s">({member.instrument})</span>
                                                    </label>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <ErrorMessage err={err}/>
                    <SuccessMessage onHide={onClose} expand={false} success={success}>¡Guardado correctamente!</SuccessMessage>
                    <LoadingBox expand={false}>
                        <DefaultFooter cancelOnly={true} onClose={onClose}/>
                        <button className="uk-button uk-button-primary" type="submit"><i className="fa fa-save"></i> <span className="hide-s">Guardar</span></button>
                    </LoadingBox>
                </SmartForm>
            </div>
        )
    }
}

export const ControlDetail = {
    Header: () => <Fragment><i className="fas fa-clipboard-list"></i> Control de Asistencia</Fragment>,
    Body: ({ onClose, elem }) => {
        if (!elem || !elem.event) return null
        
        const { event, missing } = elem
        return (
            <div className="uk-modal-body">
                <div className="om-body">
                    <CloseButton onClick={onClose} />
                    <p><i className="fas fa-tag"></i> <b>Tipo asignado</b> {event.type} &nbsp;&nbsp;<i className="fas fa-map-marked-alt"></i> <b>Ubicación</b> {event.loc}</p>
                    <p><i className="fas fa-clipboard-list"></i> <b>Descripción o Motivo</b> {event.desc}</p>
                    <hr />
                    {missing && missing.length === 0 ? (
                        <p className="uk-text-center">¡Tenemos muy buenas noticias para ti! No encontramos ninguna falta de asistencia para ese día 🤓</p>
                    ) : (
                        <Fragment>
                            <p className="uk-text-bold">Faltas de asistencia:</p>
                            <div className="uk-grid-small" uk-grid="">
                                {missing.map((u, i) => (
                                    <Fragment key={i}>
                                        <div className="uk-width-4-5">
                                            <ul className="uk-list uk-margin-remove">
                                                <li><i className="fas fa-caret-right"></i> {u.name} {u.surnames}</li>
                                            </ul>
                                        </div>
                                        <div className="uk-width-1-5 uk-text-right">
                                            <span className="hide-s">{u.instrument}</span>
                                        </div>
                                    </Fragment>
                                ))}
                            </div>
                            <p className="uk-text-small uk-text-muted uk-margin-top">AVISO: En la versión móvil no se mostrarán los datos adicionales.</p>
                        </Fragment>
                    )}
                </div>
            </div>
        )
    }
}

export const AttendanceChart = ({ controls }) => {
    if (!controls || controls.length === 0) return <p>No hay datos disponibles.</p>
    
    const sortedControls = [...controls].sort((a, b) => a.when - b.when)
    const dates = sortedControls.map(c => new Date(c.when).toLocaleDateString('es', { month: 'short', day: 'numeric' }))
    const values = sortedControls.map(c => c.missing.length)
    
    const chartData = {
        labels: dates,
        datasets: [{
            label: 'Faltas',
            backgroundColor: '#0070d9',
            borderColor: '#0070D9',
            borderWidth: 2,
            data: values
        }]
    }
    
    const chartOptions = {
        responsive: true,
        plugins: { legend: { display: false } },
        scales: {
            x: { grid: { display: true } },
            y: { min: 0, grid: { display: true } }
        }
    }
    
    return <Bar data={chartData} options={chartOptions} />
}
